import type { RateMultiplier, ShortTurnConfig, Role } from '@/types';
import type { ShiftData, RestConnector } from '@/components/config_forms/shared/help-graphics/ShiftTimelineComparison';
import type { RestGapScenario } from '@/components/config_forms/shared/help-graphics/RestGapComparison';
import type { PremiumBracket } from '@/components/config_forms/shared/help-graphics/HourlyPremiumBreakdown';
import {
  formatMultiplier,
  getThreshold,
  getSampleRole,
  getRatedRoles,
  resolveRole,
  getStraightTimeMultiplier,
  getSortedOvertimeMultipliers,
  calculatePay,
  calculatePayWithSurcharge,
  getRateProgressionInfo,
  type RateCalculationContext,
  type PayBucket,
  type PremiumPayCalculationRow,
  generatePayCalculationRows,
  generatePremiumPayCalculationRows,
  getMultiplierListStringHorizontal
} from '@/utils/rateCalculations';
import { normalizeExtendedTiers } from '@/utils/extendedOvertime';

function buildPremiumBrackets(
  rows: PremiumPayCalculationRow[],
  rateMultipliers: RateMultiplier[],
): PremiumBracket[] {
  const straightTimeCode = rateMultipliers.find(m => m.code === 'STRAIGHT_TIME');
  const straightTimeMultiplier = straightTimeCode?.defaultMultiplier ?? 1;

  return rows.map(row => {
    // hours_worked format: "0-10h" (hyphen separator, h suffix)
    const hoursStr = row.hours_worked.replace(/h$/, '');
    const parts = hoursStr.split('-');
    const start = parseFloat(parts[0] ?? '0');
    const end = parseFloat(parts[1] ?? parts[0] ?? '0');
    const hours = end - start;

    // multiplier field has "x" suffix: "1.5x"
    const multiplier = parseFloat(row.multiplier.replace(/x$/, ''));

    // base_gross contains the bracket pay before premium (may have <br/> HTML)
    const basePayStr = (row.base_gross ?? '').split('<br/>')[0].replace(/[$,]/g, '');
    const basePay = parseFloat(basePayStr || '0');

    // premium_amount may contain <br/> HTML in penalized rows
    const premiumAmountStr = (row.premium_amount ?? '').split('<br/>')[0].replace(/[$,]/g, '');
    const premiumAmount = parseFloat(premiumAmountStr || '0');

    const premiumPerHour = parseFloat(row.uplift_per_hour?.replace(/,/g, '') || '0');

    return {
      hoursLabel: row.hours_worked,
      hours,
      tierName: row.rate_name,
      multiplier,
      elevated: multiplier > straightTimeMultiplier,
      inViolationWindow: row.premium_calculation !== 'No Violation',
      basePay,
      premiumAmount,
      premiumPerHour,
      formula: row.premium_calculation === 'No Violation' ? '' : row.premium_calculation || '',
    };
  });
}

export function generateMarkdownVariables(
  rateMultipliers: RateMultiplier[],
  shortTurnConfig: ShortTurnConfig,
  roles: Role[],
  minimumBillableHours?: number | null,
  overrideRoleIndex?: number,
) {
  const ratedRoles = getRatedRoles(roles);
  const defaultSampleRole = getSampleRole(roles);
  const context: RateCalculationContext = { roles, rateMultipliers, minimumBillableHours, overrideRoleIndex };
  const sampleRole = resolveRole(context);
  const billingStructure = sampleRole.billingStructure;

  // Use the default role's billing structure for shift durations so they stay
  // constant when cycling between roles (only dollar amounts should change).
  const durationBillingStructure = defaultSampleRole.billingStructure;

  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);

  // Single-source-of-truth offset index shared between the top OffsetComparisonTimeline
  // graphic and the fixed-offset shift-example calculation. Clamps stale / NaN
  // overtimeOffset values so both render paths always see the same value.
  const originalMultipliers = [straightTimeMultiplier, ...ladder];
  const maxOffsetIndex = Math.max(0, originalMultipliers.length - 1);
  const rawBump = Number(shortTurnConfig.overtimeOffset);
  const sanitizedBump = Number.isFinite(rawBump) && rawBump >= 1 ? Math.floor(rawBump) : 1;
  const effectiveOffsetIndex = maxOffsetIndex >= 1
    ? Math.min(sanitizedBump, maxOffsetIndex)
    : 0;

  // Extended-OT tiers propagated to every surcharge-mode calc call so the shift
  // example uses the same effective ladder as the top graphic. Applies to
  // carry_over, bump_up, and offset_overtime — any mode where the surcharge can
  // push hours past the natural ladder's top rate.
  const exampleExtendedOvertimeTiers = shortTurnConfig.extendedOvertimeEnabled
    ? (shortTurnConfig.extendedOvertimeTiers ?? [])
    : [];

  // Select the best multiplier for fixed_offset examples
  // Use the selected multiplier if available, otherwise fall back to automatic selection
  const fixedOffsetExampleMultiplier = (() => {
    if (shortTurnConfig.surchargeType === 'offset_overtime' && maxOffsetIndex >= 1) {
      return originalMultipliers[effectiveOffsetIndex];
    }

    if (shortTurnConfig.floorTierCode) {
      const selectedMultiplier = rateMultipliers.find(m => m.code === shortTurnConfig.floorTierCode);
      if (selectedMultiplier) {
        return selectedMultiplier;
      }
    }

    const multipliersWithoutThresholds = rateMultipliers.filter(m =>
      m.code !== 'STRAIGHT_TIME' &&
      m.defaultPerShiftThresholdHours == null &&
      m.defaultPerWeekThresholdHours == null
    );

    if (multipliersWithoutThresholds.length > 0) {
      return multipliersWithoutThresholds[0];
    }

    const nonStraightTimeMultipliers = rateMultipliers.filter(m => m.code !== 'STRAIGHT_TIME');
    return nonStraightTimeMultipliers.length > 0 ? nonStraightTimeMultipliers[0] : straightTimeMultiplier;
  })();

  const multiplierFloorExampleMultiplier = (() => {
    if (shortTurnConfig.floorTierCode) {
      const selectedMultiplier = rateMultipliers.find(m => m.code === shortTurnConfig.floorTierCode);
      if (selectedMultiplier) {
        return selectedMultiplier;
      }
    }
    // Handle custom multiplier value (user typed a value like 1.7x instead of selecting a preset)
    if (shortTurnConfig.floorTierCustomValue) {
      const val = shortTurnConfig.floorTierCustomValue;
      return {
        code: '__CUSTOM_FLOOR__',
        name: `Guaranteed ${val}x`,
        defaultMultiplier: val,
        defaultPerShiftThresholdHours: null,
        defaultPerWeekThresholdHours: null,
        perShiftBillingStructures: null,
        perWeekBillingStructures: null,
      } satisfies RateMultiplier;
    }
    // Fallback logic if none is selected, similar to fixedOffsetExampleMultiplier
    const nonStraightTimeMultipliers = rateMultipliers.filter(m => m.code !== 'STRAIGHT_TIME');
    return nonStraightTimeMultipliers.length > 0 ? nonStraightTimeMultipliers[0] : straightTimeMultiplier;
  })();

  // When a custom floor multiplier is used, inject it into a floor-specific context
  // so the calculation engine can find it by code during rateMultipliers.find()
  const floorContext: RateCalculationContext = multiplierFloorExampleMultiplier.code === '__CUSTOM_FLOOR__'
    ? { ...context, rateMultipliers: [...rateMultipliers, multiplierFloorExampleMultiplier as RateMultiplier] }
    : context;

  const multiplier_list_str = getMultiplierListStringHorizontal(rateMultipliers, billingStructure);

  const req_break = Number(shortTurnConfig.requiredBreakHours) || 10;
  const rest_period_short = Math.max(1, req_break - 2);
  const rest_period_long = req_break + 2;
  const violationHours = req_break - rest_period_short;

  const exampleRate = sampleRole.rate || 30;
  const isDayRate = sampleRole.billingStructure === 'day_rate';

  // Find the first overtime multiplier with a threshold for examples
  const firstMultiplierWithThreshold = rateMultipliers
    .filter(m => {
      if (m.code === 'STRAIGHT_TIME') return false;
      const threshold = isDayRate ? m.defaultPerShiftThresholdHours : m.defaultPerWeekThresholdHours;
      return threshold != null && threshold > 0;
    })
    .sort((a, b) => {
      const thresholdA = isDayRate ? a.defaultPerShiftThresholdHours : a.defaultPerWeekThresholdHours;
      const thresholdB = isDayRate ? b.defaultPerShiftThresholdHours : b.defaultPerWeekThresholdHours;
      return (thresholdA ?? Infinity) - (thresholdB ?? Infinity);
    })[0];

  const hasAnyOvertimeMultiplier = rateMultipliers.some(m => m.code !== 'STRAIGHT_TIME');
  const otMultiplier = firstMultiplierWithThreshold || rateMultipliers.find(m => m.defaultMultiplier === 1.5);
  const otRate = (otMultiplier && hasAnyOvertimeMultiplier) ? (otMultiplier.defaultMultiplier ?? 1.0) : 1.0;

  const otThreshold = (() => {
    if (!hasAnyOvertimeMultiplier) {
      return Infinity;
    }
    if (firstMultiplierWithThreshold) {
      const threshold = isDayRate ? firstMultiplierWithThreshold.defaultPerShiftThresholdHours : firstMultiplierWithThreshold.defaultPerWeekThresholdHours;
      if (threshold != null && threshold > 0) return threshold;
    }
    return isDayRate ? (minimumBillableHours || 10) : 40;
  })();

  const dayRateGuaranteedHours = isDayRate ? (minimumBillableHours ?? 8) : otThreshold;
  const hourlyRateForCalc = isDayRate ? (dayRateGuaranteedHours > 0 && isFinite(dayRateGuaranteedHours) ? exampleRate / dayRateGuaranteedHours : exampleRate) : exampleRate;
  const formattedHourly = `$${hourlyRateForCalc.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const roleContext = isDayRate
    ? `${sampleRole.name} @ ${formattedHourly}/hr ($${exampleRate.toLocaleString()}/day ÷ ${dayRateGuaranteedHours}h Minimum)`
    : `${sampleRole.name} @ ${formattedHourly}/hr`;

  // --- Carry Over Calculation ---
  // Shift durations are derived from the default role's billing structure so
  // they remain constant when cycling between roles (only dollars change).
  const durationLadder = getSortedOvertimeMultipliers(rateMultipliers, durationBillingStructure);
  let cumulativeHours = 0;

  // Derive example durations to explicitly demonstrate Carry Over fallback:
  // Shift 1 ends at Straight Time, Shift 2 starts at first OT tier and reaches
  // the next tier, then Shift 3 compounds from Shift 2's final tier.
  const firstOtThreshold = (durationLadder.length > 0 ? getThreshold(durationLadder[0], durationBillingStructure) : null) ?? 10;
  const secondOtThreshold = (() => {
    const raw = durationLadder.length > 1 ? getThreshold(durationLadder[1], durationBillingStructure) : null;
    return raw != null && raw > firstOtThreshold ? raw : firstOtThreshold + 2;
  })();
  const weeklyLadder = durationBillingStructure === 'hourly';

  // Daily ladders reset each shift, so use per-shift durations.
  // Weekly ladders carry cumulative hours, so keep Shift 2 below the first
  // threshold cumulatively and have Shift 3 cross it.
  const shift1_work_period = weeklyLadder
    ? Math.max(1, Math.floor(firstOtThreshold * 0.6))
    : Math.max(1, firstOtThreshold - 2);

  const shift2_work_period = weeklyLadder
    ? Math.max(1, firstOtThreshold - shift1_work_period - 2)
    : durationLadder.length > 1
      ? Math.max(1, firstOtThreshold + 1)
      : Math.max(1, firstOtThreshold);

  const shift3_work_period = weeklyLadder
    ? Math.max(3, firstOtThreshold - (shift1_work_period + shift2_work_period) + 1)
    : Math.max(firstOtThreshold + 1, secondOtThreshold - 1);

  const shift4_work_period = shift3_work_period + 2;

  const shift1_info = getRateProgressionInfo(shift1_work_period, context, { startingHoursInWeek: cumulativeHours });
  cumulativeHours += shift1_work_period;

  const shift2_info = getRateProgressionInfo(shift2_work_period, context, { mode: 'carry_over', previousShiftFinalRateCode: shift1_info.finalRateCode, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours, startingHoursInWeek: cumulativeHours, extendedOvertimeTiers: exampleExtendedOvertimeTiers });
  cumulativeHours += shift2_work_period;

  const shift3_info = getRateProgressionInfo(shift3_work_period, context, { mode: 'carry_over', previousShiftFinalRateCode: shift2_info.finalRateCode, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours, startingHoursInWeek: cumulativeHours, extendedOvertimeTiers: exampleExtendedOvertimeTiers });
  cumulativeHours += shift3_work_period;

  const shift4_info = getRateProgressionInfo(shift4_work_period, context, { startingHoursInWeek: cumulativeHours });

  // Calculate base pay using unpenalized progression (matching the unpenalized rate progression)
  const shift1_base_pay = calculatePay(shift1_work_period, context, 0).totalPay;
  const shift2_base_pay = calculatePay(shift2_work_period, context, shift1_work_period).totalPay;
  const shift3_base_pay = calculatePay(shift3_work_period, context, shift1_work_period + shift2_work_period).totalPay;
  const shift4_base_pay = calculatePay(shift4_work_period, context, shift1_work_period + shift2_work_period + shift3_work_period).totalPay;

  // Generate unpenalized progression for comparison
  let unpenalizedCumulativeHours = 0;
  const shift1_info_unpenalized = getRateProgressionInfo(shift1_work_period, context, { startingHoursInWeek: unpenalizedCumulativeHours });
  unpenalizedCumulativeHours += shift1_work_period;
  
  const shift2_info_unpenalized = getRateProgressionInfo(shift2_work_period, context, { startingHoursInWeek: unpenalizedCumulativeHours });
  unpenalizedCumulativeHours += shift2_work_period;
  
  const shift3_info_unpenalized = getRateProgressionInfo(shift3_work_period, context, { startingHoursInWeek: unpenalizedCumulativeHours });
  unpenalizedCumulativeHours += shift3_work_period;
  
  const shift4_info_unpenalized = getRateProgressionInfo(shift4_work_period, context, { startingHoursInWeek: unpenalizedCumulativeHours });

  // For fixed dollar surcharge examples, use specific work hours that match the markdown table
  const fixed_dollar_shift1_hours = 12;
  const fixed_dollar_shift2_hours = 9;
  const fixed_dollar_shift3_hours = 10;
  const fixed_dollar_shift4_hours = 8;
  
  // Reset cumulative hours for fixed dollar examples
  let fixedDollarCumulativeHours = 0;
  const fixed_dollar_shift1_pay = calculatePay(fixed_dollar_shift1_hours, context, fixedDollarCumulativeHours).totalPay;
  fixedDollarCumulativeHours += fixed_dollar_shift1_hours;
  const fixed_dollar_shift2_pay = calculatePay(fixed_dollar_shift2_hours, context, fixedDollarCumulativeHours).totalPay;
  fixedDollarCumulativeHours += fixed_dollar_shift2_hours;
  const fixed_dollar_shift3_pay = calculatePay(fixed_dollar_shift3_hours, context, fixedDollarCumulativeHours).totalPay;
  fixedDollarCumulativeHours += fixed_dollar_shift3_hours;
  const fixed_dollar_shift4_pay = calculatePay(fixed_dollar_shift4_hours, context, fixedDollarCumulativeHours).totalPay;

  // Use fallback value for fixed surcharge amount in calculations
  const effectiveFixedSurchargeAmount = Number(shortTurnConfig.shortTurnFixedSurchargeAmount) || 75.00;

  // Rest hours between fixed dollar example shifts
  const fixed_dollar_rest1 = 6;   // Shift 1→2: short turn triggered
  const fixed_dollar_rest2 = 7;   // Shift 2→3: short turn triggered
  const fixed_dollar_rest3 = 12;  // Shift 3→4: adequate rest

  const fixed_dollar_triggered2 = fixed_dollar_rest1 < req_break;
  const fixed_dollar_triggered3 = fixed_dollar_rest2 < req_break;
  const fixed_dollar_triggered4 = fixed_dollar_rest3 < req_break;

  const fixedPremiumBreakdownData = {
    shifts: [
      {
        label: 'Shift 1',
        workHours: fixed_dollar_shift1_hours,
        restHours: null as number | null,
        surchargeTriggered: false,
        basePay: Number(fixed_dollar_shift1_pay),
        totalPay: Number(fixed_dollar_shift1_pay),
      },
      {
        label: 'Shift 2',
        workHours: fixed_dollar_shift2_hours,
        restHours: fixed_dollar_rest1,
        surchargeTriggered: fixed_dollar_triggered2,
        basePay: Number(fixed_dollar_shift2_pay),
        totalPay: Number(fixed_dollar_shift2_pay) + (fixed_dollar_triggered2 ? effectiveFixedSurchargeAmount : 0),
      },
      {
        label: 'Shift 3',
        workHours: fixed_dollar_shift3_hours,
        restHours: fixed_dollar_rest2,
        surchargeTriggered: fixed_dollar_triggered3,
        basePay: Number(fixed_dollar_shift3_pay),
        totalPay: Number(fixed_dollar_shift3_pay) + (fixed_dollar_triggered3 ? effectiveFixedSurchargeAmount : 0),
      },
      {
        label: 'Shift 4',
        workHours: fixed_dollar_shift4_hours,
        restHours: fixed_dollar_rest3,
        surchargeTriggered: fixed_dollar_triggered4,
        basePay: Number(fixed_dollar_shift4_pay),
        totalPay: Number(fixed_dollar_shift4_pay) + (fixed_dollar_triggered4 ? effectiveFixedSurchargeAmount : 0),
      },
    ],
    surchargeAmount: effectiveFixedSurchargeAmount,
    thresholdHours: req_break,
    roleContext,
  };

  // Calculate surcharged pay for carry over mode (reset cumulative hours for surcharge calculations)
  let surchargeCumulativeHours = shift1_work_period;
  const shift2_penalized_pay = calculatePayWithSurcharge(shift2_work_period, context, {
    mode: 'carry_over',
    previousShiftFinalRateCode: shift1_info.finalRateCode,
       surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours,
    extendedOvertimeTiers: exampleExtendedOvertimeTiers,
  }).totalPay;

  surchargeCumulativeHours += shift2_work_period;
  const shift3_penalized_pay = calculatePayWithSurcharge(shift3_work_period, context, {
    mode: 'carry_over',
    previousShiftFinalRateCode: shift2_info.finalRateCode,
       surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours,
    extendedOvertimeTiers: exampleExtendedOvertimeTiers,
  }).totalPay;

  const shift1_components = calculatePay(shift1_work_period, context, 0);
  const shift2_components = calculatePay(shift2_work_period, context, shift1_work_period);
  const shift3_components = calculatePay(shift3_work_period, context, shift1_work_period + shift2_work_period);
  
  // --- Bump Up Calculation ---
  const shift1_info_accel = shift1_info;
  const shift2_info_accel = getRateProgressionInfo(shift2_work_period, context, { mode: 'bump_up', previousShiftFinalRateCode: shift1_info_accel.finalRateCode, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours, extendedOvertimeTiers: exampleExtendedOvertimeTiers });
  const shift3_info_accel = getRateProgressionInfo(shift3_work_period, context, { mode: 'bump_up', previousShiftFinalRateCode: shift2_info_accel.finalRateCode, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours, extendedOvertimeTiers: exampleExtendedOvertimeTiers });
  const shift4_info_accel = shift4_info;

  // Calculate surcharged pay for bump up mode
  surchargeCumulativeHours = shift1_work_period;
  const shift2_penalized_pay_accel = calculatePayWithSurcharge(shift2_work_period, context, {
    mode: 'bump_up',
    previousShiftFinalRateCode: shift1_info.finalRateCode,
       surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours,
    extendedOvertimeTiers: exampleExtendedOvertimeTiers,
  }).totalPay;

  surchargeCumulativeHours += shift2_work_period;
  const shift3_penalized_pay_accel = calculatePayWithSurcharge(shift3_work_period, context, {
    mode: 'bump_up',
    previousShiftFinalRateCode: shift2_info_accel.finalRateCode,
       surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours,
    extendedOvertimeTiers: exampleExtendedOvertimeTiers,
  }).totalPay;

  // --- Fixed Starting Multiplier Calculation ---
  const shift2_info_fixed = getRateProgressionInfo(shift2_work_period, context, { mode: 'offset_overtime', fixedStartingMultiplierCode: fixedOffsetExampleMultiplier.code, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours, extendedOvertimeTiers: exampleExtendedOvertimeTiers });
  const shift3_info_fixed = getRateProgressionInfo(shift3_work_period, context, { mode: 'offset_overtime', fixedStartingMultiplierCode: fixedOffsetExampleMultiplier.code, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours, extendedOvertimeTiers: exampleExtendedOvertimeTiers });

  // Calculate surcharged pay for fixed starting multiplier mode
  surchargeCumulativeHours = shift1_work_period;
  const shift2_penalized_pay_fixed = calculatePayWithSurcharge(shift2_work_period, context, {
    mode: 'offset_overtime',
    fixedStartingMultiplierCode: fixedOffsetExampleMultiplier.code,
       surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours,
    extendedOvertimeTiers: exampleExtendedOvertimeTiers,
  }).totalPay;

  surchargeCumulativeHours += shift2_work_period;
  const shift3_penalized_pay_fixed = calculatePayWithSurcharge(shift3_work_period, context, {
    mode: 'offset_overtime',
    fixedStartingMultiplierCode: fixedOffsetExampleMultiplier.code,
       surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours,
    extendedOvertimeTiers: exampleExtendedOvertimeTiers,
  }).totalPay;

  // --- Rate Floor Calculation ---
  const shift2_info_floor = getRateProgressionInfo(shift2_work_period, floorContext, { mode: 'guaranteed_rate', fixedStartingMultiplierCode: multiplierFloorExampleMultiplier.code, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours });
  const shift3_info_floor = getRateProgressionInfo(shift3_work_period, floorContext, { mode: 'guaranteed_rate', fixedStartingMultiplierCode: multiplierFloorExampleMultiplier.code, surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly, violationHours });

  // Calculate surcharged pay for rate floor mode
  surchargeCumulativeHours = shift1_work_period;
  const shift2_penalized_pay_floor = calculatePayWithSurcharge(shift2_work_period, floorContext, {
    mode: 'guaranteed_rate',
    fixedStartingMultiplierCode: multiplierFloorExampleMultiplier.code,
    surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours
  }).totalPay;

  surchargeCumulativeHours += shift2_work_period;
  const shift3_penalized_pay_floor = calculatePayWithSurcharge(shift3_work_period, floorContext, {
    mode: 'guaranteed_rate',
    fixedStartingMultiplierCode: multiplierFloorExampleMultiplier.code,
    surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumBasis: shortTurnConfig.premiumBasis,
    violationHours,
    startingHoursInWeek: surchargeCumulativeHours
  }).totalPay;

  // --- Violation Only Progression Calculation ---
  const straightTimeHoursInShift = Math.min(8, shift1_work_period); // Hours that would normally be at straight time rate
  const overtimeHoursInShift = Math.max(0, shift1_work_period - 8); // Hours that would normally be at overtime rate
  
  // For violation-only mode: move violation hours from straight time to overtime tier
  const adjustedStraightTimeHours = Math.max(0, straightTimeHoursInShift - violationHours);
  const adjustedOvertimeHours = overtimeHoursInShift + violationHours;
  
  const violationOnlyProgression = adjustedStraightTimeHours > 0 && adjustedOvertimeHours > 0
    ? `${adjustedStraightTimeHours} hrs @ **${formatMultiplier(straightTimeMultiplier as RateMultiplier)}**<br/>↓<br/>${adjustedOvertimeHours} hrs @ **${ladder[0] ? formatMultiplier(ladder[0]) : formatMultiplier(straightTimeMultiplier as RateMultiplier)}**`
    : adjustedStraightTimeHours > 0
    ? `${adjustedStraightTimeHours} hrs @ **${formatMultiplier(straightTimeMultiplier as RateMultiplier)}**`
    : `${adjustedOvertimeHours} hrs @ **${ladder[0] ? formatMultiplier(ladder[0]) : formatMultiplier(straightTimeMultiplier as RateMultiplier)}**`;

  // Helper to extract calculation components from progressionWithPay
  const extractCalc = (progressionWithPay: string) => {
    // Capture pairs of (Multiplier Name, $Amount) from segments like:
    // "**Overtime ($300.00)**" or "**Double Time ($420) - Natural Progression**"
    const pairs = [...progressionWithPay.matchAll(/\*\*([^()]+?)\s*\(\$([0-9.,]+)\)[^*]*\*\*/g)];
    if (pairs.length) {
      const parts = pairs.map(([, name, amount]) => `$${amount} ${name.trim()}`);
      return `(${parts.join(' + ')})`;
    }
    // Fallback: amounts-only (in case formatting differs unexpectedly)
    const amounts = progressionWithPay.match(/\$[0-9.,]+/g) || [];
    return amounts.length ? `(${amounts.join(' + ')})` : '';
  };

  // Helper to fix upstream formatting bugs in progression strings by checking against the correct total.
  const fixBrokenProgressionString = (prog: string, totalPay: number): string => {
    const pairs = [...prog.matchAll(/\(\$([0-9.,]+)\)/g)];
    if (pairs.length === 0) return prog;
  
    const amounts = pairs.map(p => parseFloat(p[1].replace(/,/g, '')));
    const sumOfAmounts = amounts.reduce((sum, val) => sum + val, 0);
  
    if (Math.abs(sumOfAmounts - totalPay) < 1) {
      return prog; // Sum is correct, no fix needed.
    }
  
    // Try to find one number that, if multiplied by 1000, makes the sum correct.
    for (let i = 0; i < amounts.length; i++) {
      const originalAmount = amounts[i];
      if (originalAmount >= 1000) continue; // Assume large numbers are already correct.
  
      const newAmounts = [...amounts];
      newAmounts[i] = originalAmount * 1000;
      const newSum = newAmounts.reduce((sum, val) => sum + val, 0);
  
      if (Math.abs(newSum - totalPay) < 1) {
        // We found the culprit. Replace it in the original string.
        const originalAmountString = pairs[i][1];
        const newAmountString = (originalAmount * 1000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        return prog.replace(`($${originalAmountString})`, `($${newAmountString})`);
      }
    }
    
    return prog; // Unable to fix
  };

  const sustained_shift1_base_calc = extractCalc(shift1_info_unpenalized.progressionWithPay);
  const sustained_shift2_base_calc = extractCalc(shift2_info_unpenalized.progressionWithPay);
  const sustained_shift3_base_calc = extractCalc(shift3_info_unpenalized.progressionWithPay);
  const sustained_shift4_base_calc = extractCalc(shift4_info_unpenalized.progressionWithPay);
  
  // Calculations for penalized gross pay (carry over)
  const sustained_shift2_progression_corrected = fixBrokenProgressionString(shift2_info.progressionWithPay, shift2_penalized_pay);
  const sustained_shift3_progression_corrected = fixBrokenProgressionString(shift3_info.progressionWithPay, shift3_penalized_pay);
  const sustained_shift2_penalized_calc = extractCalc(sustained_shift2_progression_corrected);
  const sustained_shift3_penalized_calc = extractCalc(sustained_shift3_progression_corrected);
  
  // Calculations for surcharge amounts (carry over)
  const sustained_shift2_surcharge_calc = `($${Number(shift2_penalized_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift2_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;
  const sustained_shift3_surcharge_calc = `($${Number(shift3_penalized_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift3_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;

  // Bucket arrays for carry over
  const sustained_shift2_baseBuckets = shift2_info_unpenalized.segments;
  const sustained_shift2_surchargedBuckets = shift2_info.segments;
  const sustained_shift3_baseBuckets = shift3_info_unpenalized.segments;
  const sustained_shift3_surchargedBuckets = shift3_info.segments;

  // Calculations for penalized gross pay (bump up)
  const accelerated_shift2_progression_corrected = fixBrokenProgressionString(shift2_info_accel.progressionWithPay, shift2_penalized_pay_accel);
  const accelerated_shift3_progression_corrected = fixBrokenProgressionString(shift3_info_accel.progressionWithPay, shift3_penalized_pay_accel);
  const accelerated_shift2_penalized_calc = extractCalc(accelerated_shift2_progression_corrected);
  const accelerated_shift3_penalized_calc = extractCalc(accelerated_shift3_progression_corrected);
  
  // Calculations for surcharge amounts (bump up)
  const accelerated_shift2_surcharge_calc = `($${Number(shift2_penalized_pay_accel).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift2_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;
  const accelerated_shift3_surcharge_calc = `($${Number(shift3_penalized_pay_accel).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift3_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;

  // Bucket arrays for bump up and fixed
  const accelerated_shift2_surchargedBuckets = shift2_info_accel.segments;
  const accelerated_shift3_surchargedBuckets = shift3_info_accel.segments;
  const fixed_shift2_surchargedBuckets = shift2_info_fixed.segments;

  // Calculations for penalized gross pay (fixed starting multiplier)
  const fixed_shift2_penalized_calc = extractCalc(shift2_info_fixed.progressionWithPay);
  const fixed_shift3_penalized_calc = extractCalc(shift3_info_fixed.progressionWithPay);
  
  // Calculations for surcharge amounts (fixed starting multiplier)
  const fixed_shift2_surcharge_calc = `($${Number(shift2_penalized_pay_fixed).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift2_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;
  const fixed_shift3_surcharge_calc = `($${Number(shift3_penalized_pay_fixed).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift3_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;

  // Calculations for penalized gross pay (rate floor)
  const floor_shift2_penalized_calc = extractCalc(shift2_info_floor.progressionWithPay);
  const floor_shift3_penalized_calc = extractCalc(shift3_info_floor.progressionWithPay);
  
  // Calculations for surcharge amounts (rate floor)
  const floor_shift2_surcharge_calc = `($${Number(shift2_penalized_pay_floor).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift2_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;
  const floor_shift3_surcharge_calc = `($${Number(shift3_penalized_pay_floor).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} - $${Number(shift3_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;
  const floor_shift2_surchargedBuckets = shift2_info_floor.segments;

  // --- Hourly Premium Calculation ---
  // Use a shift long enough to cross at least one OT threshold so the
  // compounding effect of OT-Inclusive basis is visible in the breakdown.
  const hourly_premium_example_hours = shift2_work_period;
  const hourly_premium_base_pay_info = calculatePay(hourly_premium_example_hours, context, 0);

  const normal_calculation_rows = generatePayCalculationRows(hourly_premium_example_hours, context);

  const hourly_premium_penalized_pay_info_final = calculatePayWithSurcharge(hourly_premium_example_hours, context, {
    mode: 'hourly_premium',
    premiumType: shortTurnConfig.premiumType || 'fixed',
    premiumValue: Number(shortTurnConfig.premiumValue) || 0,
    surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    violationHours: violationHours,
    premiumBasis: shortTurnConfig.premiumBasis,
    startingHoursInWeek: 0
  });

  const hourly_premium_calculation_rows = generatePremiumPayCalculationRows(hourly_premium_example_hours, context, {
    premiumType: shortTurnConfig.premiumType || 'fixed',
    premiumValue: shortTurnConfig.premiumValue,
    premiumBasis: shortTurnConfig.premiumBasis || 'base_rate',
    surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    violationHours: violationHours,
  });
  
  const hourly_premium_total_premium = hourly_premium_penalized_pay_info_final.totalPay - hourly_premium_base_pay_info.totalPay;

  const premiumBrackets = buildPremiumBrackets(hourly_premium_calculation_rows, rateMultipliers);

  // Base buckets (normal pay without surcharge) from rate progression
  const hourly_premium_base_progression = getRateProgressionInfo(hourly_premium_example_hours, context, { startingHoursInWeek: 0 });
  const hourly_premium_baseBuckets: PayBucket[] = hourly_premium_base_progression.segments;

  // Surcharged buckets — split each bracket into a base-pay line + a premium line.
  // The premium line uses customLabel to render as "Xh × Premium ($25/hr × Xh)".
  // Sort so violation (premium) brackets come first — the short turn surcharge
  // applies from the start of the shift, not the end.
  const sortedPremiumBrackets = shortTurnConfig.surchargeAppliesToShortedHoursOnly
    ? [...premiumBrackets].sort((a, b) => (b.inViolationWindow ? 1 : 0) - (a.inViolationWindow ? 1 : 0))
    : premiumBrackets;
  const hourly_premium_surchargedBuckets: PayBucket[] = sortedPremiumBrackets.flatMap(bracket => {
    const hasPremium = bracket.inViolationWindow || !shortTurnConfig.surchargeAppliesToShortedHoursOnly;
    const baseLine: PayBucket = {
      hours: bracket.hours,
      tierName: bracket.tierName,
      multiplier: bracket.multiplier,
      amount: bracket.basePay,
    };
    if (!hasPremium || bracket.premiumAmount === 0) return [baseLine];
    const isPercentage = (shortTurnConfig.premiumType || 'fixed') === 'percentage';
    const premiumVal = Number(shortTurnConfig.premiumValue) || 0;
    // For percentage: show "25% × $65" (rate the percentage applies to).
    // For fixed dollar: show "$25/hr × 10h".
    let premiumDesc: string;
    if (isPercentage) {
      const basisRate = (shortTurnConfig.premiumBasis === 'ot_inclusive')
        ? hourlyRateForCalc * bracket.multiplier
        : hourlyRateForCalc;
      const hasCents = basisRate % 1 !== 0;
      premiumDesc = `${premiumVal}% × $${basisRate.toLocaleString(undefined, { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 })}`;
    } else {
      const fixedHasCents = bracket.premiumPerHour % 1 !== 0;
      premiumDesc = `$${bracket.premiumPerHour.toLocaleString(undefined, { minimumFractionDigits: fixedHasCents ? 2 : 0, maximumFractionDigits: 2 })}/hr × ${bracket.hours}h`;
    }
    const premiumLine: PayBucket = {
      hours: bracket.hours,
      tierName: 'Premium',
      multiplier: 0,
      amount: bracket.premiumAmount,
      customLabel: `${bracket.hours}h × Premium (${premiumDesc})`,
      lifted: true,
    };
    return [baseLine, premiumLine];
  });

  const premiumBreakdownData = {
    shiftHours: hourly_premium_example_hours,
    violationHours: violationHours,
    surchargeAppliesToShortedHoursOnly: shortTurnConfig.surchargeAppliesToShortedHoursOnly,
    premiumType: (shortTurnConfig.premiumType || 'fixed') as 'percentage' | 'fixed',
    premiumValue: Number(shortTurnConfig.premiumValue) || 0,
    premiumBasis: (shortTurnConfig.premiumBasis || 'base_rate') as 'base_rate' | 'ot_inclusive',
    brackets: premiumBrackets,
    basePay: Number(hourly_premium_base_pay_info.totalPay),
    surchargedPay: Number(hourly_premium_penalized_pay_info_final.totalPay),
    surchargeAmount: hourly_premium_total_premium,
    baseBuckets: hourly_premium_baseBuckets,
    surchargedBuckets: hourly_premium_surchargedBuckets,
    roleContext,
    actualRestHours: rest_period_short,
  };
  const hourly_premium_per_hour = hourly_premium_example_hours > 0 ? hourly_premium_total_premium / hourly_premium_example_hours : 0;
  const hourly_premium_uplift_value = shortTurnConfig.premiumType === 'percentage'
    ? `${Number(shortTurnConfig.premiumValue ?? 0)}%`
    : `$${(Number(shortTurnConfig.premiumValue ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const premium_per_hour_display = (() => {
    if (shortTurnConfig.premiumType === 'percentage') {
      return shortTurnConfig.premiumValue != null && Number(shortTurnConfig.premiumValue) > 0
        ? `**${Number(shortTurnConfig.premiumValue)}%**`
        : '`Hourly Premium`';
    }
    // else it's fixed
    return shortTurnConfig.premiumValue != null && Number(shortTurnConfig.premiumValue) > 0
      ? `**$${Number(shortTurnConfig.premiumValue).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}**`
      : '`Hourly Premium`';
  })();

  // Show Shift 3 (compounding demo) only when 2+ OT tiers exist — with a single tier
  // the carry-over/bump-up just repeats the same starting rate, adding no new information.
  // Fixed Start never compounds (always same starting tier), so always skip Shift 3.
  const showCompoundingShift = ladder.length >= 2;

  const sustainedTimeline = {
    shifts: [
      {
        label: 'Shift 1',
        workHours: shift1_work_period,
        startRate: shift1_info.startingRate,
        endRate: shift1_info.finalRate,
        basePay: Number(shift1_base_pay),
        surcharged: false,
      },
      {
        label: 'Shift 2',
        workHours: shift2_work_period,
        startRate: shift2_info.startingRate,
        endRate: shift2_info.finalRate,
        basePay: Number(shift2_base_pay),
        surcharged: true,
        surchargedPay: Number(shift2_penalized_pay),
        surchargeAmount: Number(shift2_penalized_pay) - Number(shift2_base_pay),
        baseBuckets: sustained_shift2_baseBuckets,
        surchargedBuckets: sustained_shift2_surchargedBuckets,
        roleContext,
      },
      ...(showCompoundingShift ? [{
        label: 'Shift 3',
        workHours: shift3_work_period,
        startRate: shift3_info.startingRate,
        endRate: shift3_info.finalRate,
        basePay: Number(shift3_base_pay),
        surcharged: true,
        surchargedPay: Number(shift3_penalized_pay),
        surchargeAmount: Number(shift3_penalized_pay) - Number(shift3_base_pay),
        baseBuckets: sustained_shift3_baseBuckets,
        surchargedBuckets: sustained_shift3_surchargedBuckets,
        roleContext,
      }] satisfies ShiftData[] : []),
    ] satisfies ShiftData[],
    connectors: [
      { hours: rest_period_short, shortedHours: violationHours, triggered: true, previousEndRate: shift1_info.finalRate },
      ...(showCompoundingShift ? [{ hours: rest_period_short, shortedHours: violationHours, triggered: true, previousEndRate: shift2_info.finalRate }] satisfies RestConnector[] : []),
      { hours: rest_period_long, triggered: false, nextShiftRate: shift4_info.startingRate },
    ] satisfies RestConnector[],
  };

  const acceleratedTimeline = {
    shifts: [
      { label: 'Shift 1', workHours: shift1_work_period, startRate: shift1_info_accel.startingRate, endRate: shift1_info_accel.finalRate, basePay: Number(shift1_base_pay), surcharged: false },
      { label: 'Shift 2', workHours: shift2_work_period, startRate: shift2_info_accel.startingRate, endRate: shift2_info_accel.finalRate, basePay: Number(shift2_base_pay), surcharged: true, surchargedPay: Number(shift2_penalized_pay_accel), surchargeAmount: Number(shift2_penalized_pay_accel) - Number(shift2_base_pay), baseBuckets: sustained_shift2_baseBuckets, surchargedBuckets: accelerated_shift2_surchargedBuckets, roleContext },
      ...(showCompoundingShift ? [
        { label: 'Shift 3', workHours: shift3_work_period, startRate: shift3_info_accel.startingRate, endRate: shift3_info_accel.finalRate, basePay: Number(shift3_base_pay), surcharged: true, surchargedPay: Number(shift3_penalized_pay_accel), surchargeAmount: Number(shift3_penalized_pay_accel) - Number(shift3_base_pay), baseBuckets: sustained_shift3_baseBuckets, surchargedBuckets: accelerated_shift3_surchargedBuckets, roleContext },
      ] satisfies ShiftData[] : []),
    ] satisfies ShiftData[],
    connectors: [
      { hours: rest_period_short, shortedHours: violationHours, triggered: true, previousEndRate: shift1_info_accel.finalRate },
      ...(showCompoundingShift ? [{ hours: rest_period_short, shortedHours: violationHours, triggered: true, previousEndRate: shift2_info_accel.finalRate }] satisfies RestConnector[] : []),
      { hours: rest_period_long, triggered: false, nextShiftRate: shift4_info_accel.startingRate },
    ] satisfies RestConnector[],
  };

  // Fixed Start never compounds — the starting tier is always the same regardless of
  // previous shift, so Shift 3 always shows identical behavior to Shift 2.
  const fixedTimeline = {
    shifts: [
      { label: 'Shift 1', workHours: shift1_work_period, startRate: shift1_info.startingRate, endRate: shift1_info.finalRate, basePay: Number(shift1_base_pay), surcharged: false },
      { label: 'Shift 2', workHours: shift2_work_period, startRate: shift2_info_fixed.startingRate, endRate: shift2_info_fixed.finalRate, basePay: Number(shift2_base_pay), surcharged: true, surchargedPay: Number(shift2_penalized_pay_fixed), surchargeAmount: Number(shift2_penalized_pay_fixed) - Number(shift2_base_pay), baseBuckets: sustained_shift2_baseBuckets, surchargedBuckets: fixed_shift2_surchargedBuckets, roleContext },
    ] satisfies ShiftData[],
    connectors: [
      { hours: rest_period_short, shortedHours: violationHours, triggered: true },
      { hours: rest_period_long, triggered: false, nextShiftRate: shift4_info.startingRate },
    ] satisfies RestConnector[],
  };

  const floorTimeline = {
    shifts: [
      { label: 'Shift 1', workHours: shift1_work_period, startRate: shift1_info.startingRate, endRate: shift1_info.finalRate, basePay: Number(shift1_base_pay), surcharged: false },
      { label: 'Shift 2', workHours: shift2_work_period, startRate: shift2_info_floor.startingRate, endRate: shift2_info_floor.finalRate, basePay: Number(shift2_base_pay), surcharged: true, surchargedPay: Number(shift2_penalized_pay_floor), surchargeAmount: Number(shift2_penalized_pay_floor) - Number(shift2_base_pay), baseBuckets: sustained_shift2_baseBuckets, surchargedBuckets: floor_shift2_surchargedBuckets, roleContext },
    ] satisfies ShiftData[],
    connectors: [
      { hours: rest_period_short, shortedHours: violationHours, triggered: true },
      { hours: rest_period_long, triggered: false, nextShiftRate: shift4_info.startingRate },
    ] satisfies RestConnector[],
  };

  return {
    required_break_hours: req_break,
    rest_period_short,
    carry_over_single_overtime_tier: ladder.length < 2,
    surcharge_period: shortTurnConfig.surchargeAppliesToShortedHoursOnly ? `${violationHours} hours` : 'Full Shift',
    sample_role_name: sampleRole.name,
    sample_role_rate: exampleRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    sample_role_hourly_rate: hourlyRateForCalc.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    sample_role_billing_structure: isDayRate ? 'day rate' : 'hourly',
    fixed_surcharge_amount: shortTurnConfig.shortTurnFixedSurchargeAmount,
    multiplier_list_str,
    rest_period_long,
    shift1_work_period,
    shift1_progression: shift1_info.progressionWithPay,
    shift1_previous_rate: 'N/A',
    shift1_starting_rate: `**${shift1_info.startingRate}**`,
    shift2_work_period,
    shift2_progression: sustained_shift2_progression_corrected,
    shift2_previous_rate: shift1_info.finalRate,
    shift2_starting_rate: `**${shift2_info.startingRate}**`,
    shift3_work_period,
    shift3_progression: sustained_shift3_progression_corrected,
    shift3_previous_rate: shift2_info.finalRate,
    shift3_starting_rate: `**${shift3_info.startingRate}**`,
    shift4_work_period,
    shift4_progression: shift4_info.progressionWithPay,
    shift4_previous_rate: shift3_info.finalRate,
    shift4_starting_rate: `**${shift4_info.startingRate}**`,
    
    // Unpenalized progressions for comparison
    shift1_progression_unpenalized: shift1_info_unpenalized.progressionWithPay,
    shift2_progression_unpenalized: shift2_info_unpenalized.progressionWithPay,
    shift3_progression_unpenalized: shift3_info_unpenalized.progressionWithPay,
    shift4_progression_unpenalized: shift4_info_unpenalized.progressionWithPay,
    shift1_progression_accel: shift1_info_accel.progressionWithPay,
    shift1_previous_rate_accel: 'N/A',
    shift1_starting_rate_accel: `**${shift1_info_accel.startingRate}**`,
    shift2_progression_accel: accelerated_shift2_progression_corrected,
    shift2_previous_rate_accel: shift1_info_accel.finalRate,
    shift2_starting_rate_accel: `**${shift2_info_accel.startingRate}**`,
    shift3_progression_accel: accelerated_shift3_progression_corrected,
    shift3_previous_rate_accel: shift2_info_accel.finalRate,
    shift3_starting_rate_accel: `**${shift3_info_accel.startingRate}**`,
    shift4_previous_rate_accel: shift3_info_accel.finalRate,
    
    // Penalized pay for bump up
    shift2_penalized_pay_accel: Number(shift2_penalized_pay_accel).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_penalized_pay_accel: Number(shift3_penalized_pay_accel).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),

    // Short turn premium for bump up
    shift2_short_turn_premium_accel: (Number(shift2_penalized_pay_accel) - Number(shift2_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_short_turn_premium_accel: (Number(shift3_penalized_pay_accel) - Number(shift3_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),

    // Penalized pay for fixed starting multiplier
    shift2_penalized_pay_fixed: Number(shift2_penalized_pay_fixed).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_penalized_pay_fixed: Number(shift3_penalized_pay_fixed).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),

    // Short turn premium for fixed starting multiplier
    shift2_short_turn_premium_fixed: (Number(shift2_penalized_pay_fixed) - Number(shift2_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_short_turn_premium_fixed: (Number(shift3_penalized_pay_fixed) - Number(shift3_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    
    // Fixed offset example variables - implementing safety net logic
    fixed_offset_example_multiplier: formatMultiplier(fixedOffsetExampleMultiplier as RateMultiplier),
    fixed_offset_example_multiplier_name: fixedOffsetExampleMultiplier.name,
    fixed_offset_example_multiplier_rate: fixedOffsetExampleMultiplier.defaultMultiplier,
    
    // Rate Floor example
    multiplier_floor_example_multiplier: formatMultiplier(multiplierFloorExampleMultiplier as RateMultiplier),
    multiplier_floor_example_multiplier_name: multiplierFloorExampleMultiplier.name,
    
    shift1_progression_fixed: shift1_info.progressionWithPay,
    shift2_progression_fixed: shift2_info_fixed.progressionWithPay,
    shift3_progression_fixed: shift3_info_fixed.progressionWithPay,
    
    // Final rates for fixed offset calculations
    shift1_final_rate: shift1_info.finalRate,
    shift2_final_rate_fixed: shift2_info_fixed.finalRate,
    shift3_final_rate_fixed: shift3_info_fixed.finalRate,
    
    // Rate Floor progression
    shift2_progression_floor: shift2_info_floor.progressionWithPay,
    shift3_progression_floor: shift3_info_floor.progressionWithPay,

    // Penalized pay for rate floor mode
    shift2_penalized_pay_floor: Number(shift2_penalized_pay_floor).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_penalized_pay_floor: Number(shift3_penalized_pay_floor).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),

    // Short turn premium for rate floor mode
    shift2_short_turn_premium_floor: (Number(shift2_penalized_pay_floor) - Number(shift2_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_short_turn_premium_floor: (Number(shift3_penalized_pay_floor) - Number(shift3_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),

    // Violation-only progression
    shift1_progression_violation_only: violationOnlyProgression,
    
    // NEW variables for fixed_premium.md
    example_role_rate: exampleRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    example_role_name: sampleRole.name,
    example_billing_structure_unit: isDayRate ? '/day' : '/hr',
    example_ot_rate: (hourlyRateForCalc * otRate).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    example_ot_multiplier_rate: otRate,
    example_ot_multiplier_name: otMultiplier?.name || 'First Overtime',
    example_ot_threshold: isFinite(otThreshold) ? otThreshold : (isDayRate ? (minimumBillableHours || 8) : 40),
    example_ot_description: getMultiplierListStringHorizontal(rateMultipliers, billingStructure),
    minimum_billable_hours: minimumBillableHours ?? 8,

    // Structured data for FixedPremiumBreakdown component
    fixed_premium_breakdown_data: fixedPremiumBreakdownData,

    // Carry over mode base-pay (unpenalized) values for the example table
    sustained_shift1_base_pay: Number(shift1_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    sustained_shift2_base_pay: Number(shift2_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    sustained_shift3_base_pay: Number(shift3_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    sustained_shift4_base_pay: Number(shift4_base_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    
    sustained_shift1_base_calc,
    sustained_shift2_base_calc,
    sustained_shift3_base_calc,
    sustained_shift4_base_calc,
    // Penalized calculation breakdowns for table (carry over)
    sustained_shift2_penalized_calc,
    sustained_shift3_penalized_calc,
    sustained_shift2_surcharge_calc,
    sustained_shift3_surcharge_calc,
    
    // Bump up calculation breakdowns
    accelerated_shift2_penalized_calc,
    accelerated_shift3_penalized_calc,
    accelerated_shift2_surcharge_calc,
    accelerated_shift3_surcharge_calc,
    
    // Fixed starting multiplier calculation breakdowns
    fixed_shift2_penalized_calc,
    fixed_shift3_penalized_calc,
    fixed_shift2_surcharge_calc,
    fixed_shift3_surcharge_calc,
    
    // Rate floor calculation breakdowns
    floor_shift2_penalized_calc,
    floor_shift3_penalized_calc,
    floor_shift2_surcharge_calc,
    floor_shift3_surcharge_calc,

    // Floor comparison timeline data — built from the full rate ladder so all
    // configured tiers are visible, not just those the example shift reaches.
    floor_comparison_data: (() => {
      const allMultipliers = [straightTimeMultiplier, ...ladder];
      const ladderType = ladder.length > 0 && ladder[0].defaultPerShiftThresholdHours != null
        ? 'day_rate' as const : 'hourly' as const;
      const brackets = allMultipliers.map((m, i) => {
        const threshold = getThreshold(m, ladderType);
        const nextThreshold = i + 1 < allMultipliers.length
          ? getThreshold(allMultipliers[i + 1], ladderType) : threshold + 4;
        return {
          flex: Math.max(nextThreshold - threshold, 1),
          prevailingMultiplier: m.defaultMultiplier ?? 0,
          prevailingLabel: `${m.defaultMultiplier ?? 0}x`,
        };
      });
      return {
        brackets,
        floorMultiplier: multiplierFloorExampleMultiplier.defaultMultiplier ?? 0,
        floorLabel: `${multiplierFloorExampleMultiplier.defaultMultiplier ?? 0}x`,
      };
    })(),

    // Offset comparison timeline data — top graphic for offset_overtime surcharge.
    // Reuses the same effectiveOffsetIndex as the shift-example calculation so the
    // two render paths can never disagree on which tier the ladder is bumped to.
    offset_comparison_data: (() => {
      const allMultipliers = [straightTimeMultiplier, ...ladder];
      if (allMultipliers.length <= 1) return undefined;
      const ladderType = ladder.length > 0 && ladder[0].defaultPerShiftThresholdHours != null
        ? 'day_rate' as const : 'hourly' as const;
      const brackets = allMultipliers.map((m, i) => {
        const threshold = getThreshold(m, ladderType);
        const nextThreshold = i + 1 < allMultipliers.length
          ? getThreshold(allMultipliers[i + 1], ladderType) : threshold + 4;
        return {
          flex: Math.max(nextThreshold - threshold, 1),
          naturalMultiplier: m.defaultMultiplier ?? 0,
          naturalLabel: `${m.defaultMultiplier ?? 0}x`,
        };
      });
      const ladderTopMultiplier = Math.max(
        ...allMultipliers.map(m => m.defaultMultiplier ?? 0),
      );
      const normalizedExtended = shortTurnConfig.extendedOvertimeEnabled
        ? normalizeExtendedTiers(shortTurnConfig.extendedOvertimeTiers ?? [], ladderTopMultiplier)
        : [];
      const effectiveLadder = [
        ...allMultipliers.map(m => ({ multiplier: m.defaultMultiplier ?? 0, label: `${m.defaultMultiplier ?? 0}x` })),
        ...normalizedExtended.map(t => ({ multiplier: t.rate, label: `${t.rate}x` })),
      ];
      return { brackets, effectiveLadder, offsetIndex: effectiveOffsetIndex };
    })(),
    
    // Penalized pay calculations
    shift2_penalized_pay: Number(shift2_penalized_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_penalized_pay: Number(shift3_penalized_pay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    
    // Short turn premium calculations
    shift2_short_turn_premium: (Number(shift2_penalized_pay) - Number(shift2_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_short_turn_premium: (Number(shift3_penalized_pay) - Number(shift3_base_pay)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    
    // NEW variables for detailed breakdown in markdown
    shift1_ot_premium_pay: Number(shift1_components.premiumPay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift2_ot_premium_pay: Number(shift2_components.premiumPay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift2_ot_premium_pay_is_positive: Number(shift2_components.premiumPay) > 0,
    shift3_ot_premium_pay: Number(shift3_components.premiumPay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    shift3_ot_premium_pay_is_positive: Number(shift3_components.premiumPay) > 0,
    
    // NEW variables for hourly_premium.md
    hourly_premium_example_hours,
    hourly_premium_base_pay: Number(hourly_premium_base_pay_info.totalPay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    hourly_premium_penalized_pay: Number(hourly_premium_penalized_pay_info_final.totalPay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    hourly_premium_uplift_value,
    premium_per_hour_display,
    hourly_premium_premium_per_hour: Number(hourly_premium_per_hour).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    hourly_premium_base_hourly_rate: (Number(hourly_premium_base_pay_info.totalPay) / hourly_premium_example_hours).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    hourly_premium_penalized_hourly_rate: (Number(hourly_premium_penalized_pay_info_final.totalPay) / hourly_premium_example_hours).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),

    // Add the missing premium variables that the markdown template expects
    short_turn_premium_type: shortTurnConfig.premiumType || 'fixed',
    short_turn_premium_value: shortTurnConfig.premiumValue,

    // Add missing variables for the example scenario
    example_shift_duration: hourly_premium_example_hours,
    example_role_unit: isDayRate ? '/day' : '/hr',
    normal_calculation_rows,
    normal_day_total_pay: Number(hourly_premium_base_pay_info.totalPay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    hourly_premium_calculation_rows,
    short_turn_hourly_premium_total_pay: Number(hourly_premium_penalized_pay_info_final.totalPay).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    short_turn_hourly_premium_total_premium: Number(hourly_premium_total_premium).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    short_turn_premium_basis: shortTurnConfig.premiumBasis ?? 'base_rate',

    // Timeline data for ShiftTimelineComparison
    sustained_timeline: sustainedTimeline,
    bump_up_timeline: acceleratedTimeline,
    fixed_timeline: fixedTimeline,
    floor_timeline: floorTimeline,

    Math,

    // Structured bracket data for HourlyPremiumBreakdown component
    premium_breakdown_data: premiumBreakdownData,

    // Rest gap scenario data for RestGapComparison component
    rest_gap_data: (() => {
      // Anchor: Shift B always starts at 6 am Tuesday
      const shiftBStartHour = 6;
      const shiftDuration = 8;

      // Triggered: gap is 2 hours LESS than minimum
      const triggeredGap = Math.max(1, req_break - 2);
      const triggeredEndHour = ((shiftBStartHour - triggeredGap) % 24 + 24) % 24;

      // Adequate: gap is 2 hours MORE than minimum
      const adequateGap = req_break + 2;
      const adequateEndHour = ((shiftBStartHour - adequateGap) % 24 + 24) % 24;

      const formatHour = (h: number) => {
        const rounded = Math.round(h) % 24;
        const hour12 = rounded % 12 || 12;
        const ampm = rounded < 12 ? 'am' : 'pm';
        return `${hour12} ${ampm}`;
      };

      const triggered: RestGapScenario = {
        shiftALabel: 'Shift A',
        shiftADay: 'Mon',
        shiftAEndTime: formatHour(triggeredEndHour),
        shiftAHours: shiftDuration,
        shiftBLabel: 'Shift B',
        shiftBDay: 'Tue',
        shiftBStartTime: formatHour(shiftBStartHour),
        shiftBHours: shiftDuration,
        restHours: triggeredGap,
        triggered: true,
      };

      const adequate: RestGapScenario = {
        shiftALabel: 'Shift A',
        shiftADay: 'Mon',
        shiftAEndTime: formatHour(adequateEndHour),
        shiftAHours: shiftDuration,
        shiftBLabel: 'Shift B',
        shiftBDay: 'Tue',
        shiftBStartTime: formatHour(shiftBStartHour),
        shiftBHours: shiftDuration,
        restHours: adequateGap,
        triggered: false,
      };

      return {
        scenarios: [triggered, adequate] as [RestGapScenario, RestGapScenario],
        minimumRestHours: req_break,
        minimumRestInputValue: shortTurnConfig.requiredBreakHours ?? null,
      };
    })(),
  };
}
